import { reactive, readonly, watch, computed } from 'vue';
import { useLocalStorage } from '@/composables/useLocalStorage';
import { useAuthentication } from '@/composables/useAuthentication';

const CHARTLIST_SYMBOL_SUMMARY_VISIBILITY_KEY = 'sc3WorkbenchChartListSymbolSummaryRowVisibility';

const shownColumns = reactive({
  symbol: true,
  name: true,
  sector: true,
  industry: true,
  last: true,
  change: true,
  percentChange: true,
  plusMinus: true,
  volume: true,
  marketCap: true,
  sctr: true,
  universe: true,
  emaTwenty: false,
  smaFifty: true,
  smaTwoHundred: true,
  rsi: false,
  atr: false,
  adx: false,
  nextEarnings: false,
  lastEarnings: false,
  dividendTTM: false,
  yieldTTM: false,
  dividendFTM: false,
  yieldFTM: false,
  dividendExDate: false,
  epsTTM: false,
  priceOverEarnings: false,
  date: true
});

interface Column {
  title: string;
  value: keyof typeof shownColumns;
}

const COLUMNS = computed<Column[]>(() => {
  return [
    { title: 'Symbol', value: 'symbol' },
    { title: 'Name', value: 'name' },
    { title: 'Sector', value: 'sector' },
    { title: 'Industry', value: 'industry' },
    { title: 'Last', value: 'last' },
    { title: 'CHG', value: 'change' },
    { title: '% CHG', value: 'percentChange' },
    { title: '+/-', value: 'plusMinus' },
    { title: 'Volume', value: 'volume' },
    { title: 'Market Cap', value: 'marketCap' },
    { title: 'SCTR', value: 'sctr' },
    { title: 'Universe', value: 'universe' },
    { title: '+/- EMA(20)', value: 'emaTwenty' },
    { title: '+/- SMA(50)', value: 'smaFifty' },
    { title: '+/- SMA(200)', value: 'smaTwoHundred' },
    { title: 'RSI (14)', value: 'rsi' },
    { title: 'ATR (14)', value: 'atr' },
    { title: 'ADX (14)', value: 'adx' },
    { title: 'Next Earnings', value: 'nextEarnings' },
    { title: 'Last Earnings', value: 'lastEarnings' },
    { title: 'Dividend (TTM)', value: 'dividendTTM' },
    { title: 'Yield (TTM)', value: 'yieldTTM' },
    { title: 'Dividend (FTM)', value: 'dividendFTM' },
    { title: 'Yield (FTM)', value: 'yieldFTM' },
    { title: 'Ex-Dividend Date', value: 'dividendExDate' },
    { title: 'EPS (TTM)', value: 'epsTTM' },
    { title: 'P/E', value: 'priceOverEarnings' },
    { title: 'Date', value: 'date' }
  ]
});

function setColumnVisibility(columnName: keyof typeof shownColumns, isVisible: boolean) {
  shownColumns[columnName] = isVisible;
}

function toggleColumnVisibility(columnName: keyof typeof shownColumns) {
  shownColumns[columnName] = !shownColumns[columnName];
}

let hasInitialized = false;

async function init() {
  const { isLoggedIn } = useAuthentication();
  const { setLocalStorageData, getLocalStorageData } = useLocalStorage();

  if (hasInitialized) {
    return;
  }

  hasInitialized = true;

  if (!isLoggedIn.value) {
    return;
  }

  const storedVisibilityValue = getLocalStorageData(CHARTLIST_SYMBOL_SUMMARY_VISIBILITY_KEY, '');
  const storedVisibilityObject = storedVisibilityValue ? JSON.parse(storedVisibilityValue) : {};

  for (const [key, value] of Object.entries(storedVisibilityObject)) {
    if (key in shownColumns && typeof value === 'boolean') {
      shownColumns[key as keyof typeof shownColumns] = value;
    }
  }

  watch(shownColumns, () => {
    setLocalStorageData(CHARTLIST_SYMBOL_SUMMARY_VISIBILITY_KEY, JSON.stringify(shownColumns));
  });
}

export function useChartListSummary() {
  init();

  return {
    COLUMNS,
    shownColumns: readonly(shownColumns),
    setColumnVisibility,
    toggleColumnVisibility
  }
}
